import { useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

export function HostawayCommissionBackfillPanel() {
  const [running, setRunning] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [results, setResults] = useState(null);

  const run = async (dryRun) => {
    setRunning(true);
    setResults(null);
    setConfirmApply(false);

    try {
      const { data } = await diagnosticsAPI.hostawayBackfillChannelCommission({ dryRun });
      setResults(data);

      if (!dryRun && !data.error && data.commission_populated_delta > 0) {
        toast.success(
          `Commission backfill applied: ${data.commission_populated_delta} reservations updated, ` +
          `net revenue delta: ${data.net_revenue_delta}`
        );
      }
    } catch (err) {
      setResults({ error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const r = results;

  return (
    <Card data-testid="hostaway-commission-backfill-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Hostaway Channel Commission Backfill
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Re-derives channel_commission (and net_revenue) from Hostaway's financeField[] array --
          the field the ingestion fix now reads instead of the flat scalar that Booking.com never
          populates. Preview live-checks affected reservations against the real API (old vs new,
          grouped by channel) without writing. Apply re-syncs from the live API with the corrected
          extractor -- the list endpoint the sync uses never carries financeField itself, so it
          takes an extra per-reservation detail fetch for any reservation whose commission isn't
          already resolved by a flat scalar (Airbnb skips this; Booking.com and similar don't). A
          daily gap-reconciler also runs this automatically; this button is for on-demand
          verification or an immediate re-run.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Button onClick={() => run(true)} disabled={running}>
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {!confirmApply ? (
            <Button variant="destructive" onClick={() => setConfirmApply(true)} disabled={running}>
              Apply
            </Button>
          ) : (
            <Button variant="destructive" onClick={() => run(false)} disabled={running}>
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Confirm Apply
            </Button>
          )}
        </div>

        {r && (
          <div className="space-y-4">
            {r.error && (
              <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {r.error === 'no_credentials'
                  ? 'No Hostaway credentials configured -- cannot live-check candidates.'
                  : r.error}
              </div>
            )}

            {/* Preview results */}
            {r.dry_run && !r.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Would change</p>
                    <p className="text-xl font-bold tabular-nums text-amber-600 dark:text-amber-400">
                      {r.would_change_count}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Unchanged (checked)</p>
                    <p className="text-xl font-bold tabular-nums">{r.unchanged_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Candidates (DB filter)</p>
                    <p className="text-xl font-bold tabular-nums">{r.candidate_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Projected net revenue delta</p>
                    <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                      {r.projected_net_revenue_delta}
                    </p>
                  </div>
                </div>

                {r.unresolvable_count > 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    {r.unresolvable_count} candidate(s) could not be live-checked (fetch failed).
                  </p>
                )}
                {r.capped && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">
                    Live-check capped at {r.checked_count} of {r.candidate_count} candidates.
                  </p>
                )}

                {r.by_channel_would_change && Object.keys(r.by_channel_would_change).length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">Would change, by channel: </span>
                    {Object.entries(r.by_channel_would_change).map(([ch, n]) => (
                      <Badge key={ch} variant="outline" className="mr-1">{ch}: {n}</Badge>
                    ))}
                  </div>
                )}
                {r.by_channel_candidates && Object.keys(r.by_channel_candidates).length > 0 && (
                  <div className="text-sm">
                    <span className="font-medium">All candidates, by channel: </span>
                    {Object.entries(r.by_channel_candidates).map(([ch, n]) => (
                      <Badge key={ch} variant="secondary" className="mr-1">{ch}: {n}</Badge>
                    ))}
                  </div>
                )}

                <p className="text-sm text-muted-foreground">{r.message}</p>

                {r.bookings?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {r.bookings.length > MAX_DISPLAY_ROWS
                        ? `Showing ${MAX_DISPLAY_ROWS} of ${r.bookings.length} reservations`
                        : `${r.bookings.length} reservation${r.bookings.length !== 1 ? 's' : ''}`}
                    </p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-3 py-2 font-medium">ID</th>
                            <th className="text-left px-3 py-2 font-medium">Property</th>
                            <th className="text-left px-3 py-2 font-medium">Channel</th>
                            <th className="text-left px-3 py-2 font-medium">Check-in</th>
                            <th className="text-right px-3 py-2 font-medium">Gross</th>
                            <th className="text-right px-3 py-2 font-medium">Old commission</th>
                            <th className="text-right px-3 py-2 font-medium">New commission</th>
                            <th className="text-right px-3 py-2 font-medium">New net</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.bookings.slice(0, MAX_DISPLAY_ROWS).map((b, i) => (
                            <tr key={b.hostaway_id || i} className="border-b last:border-0">
                              <td className="px-3 py-2 font-mono text-xs">{b.hostaway_id}</td>
                              <td className="px-3 py-2 text-xs">{b.property_name}</td>
                              <td className="px-3 py-2">
                                <Badge variant="outline" className="text-[10px]">{b.channel}</Badge>
                              </td>
                              <td className="px-3 py-2 font-mono text-xs">{b.check_in}</td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {b.total_price != null ? Number(b.total_price).toFixed(2) : '—'}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-400">
                                {Number(b.old_commission || 0).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums font-medium">
                                {Number(b.new_commission).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {Number(b.new_net_revenue).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Apply results */}
            {r.dry_run === false && !r.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Newly populated</p>
                    <p className="text-xl font-bold tabular-nums">{r.commission_populated_delta}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Commission sum delta</p>
                    <p className="text-xl font-bold tabular-nums">{r.commission_sum_delta}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Net revenue delta</p>
                    <p className="text-xl font-bold tabular-nums text-red-600 dark:text-red-400">
                      {r.net_revenue_delta}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Pre-existing populated</p>
                    <p className="text-xl font-bold tabular-nums">{r.pre_existing_populated_count}</p>
                  </div>
                </div>

                {r.sync_result && (
                  <div className="text-sm text-muted-foreground">
                    Sync: {r.sync_result.synced} fetched, {r.sync_result.created} created,{' '}
                    {r.sync_result.updated} updated, {r.sync_result.unowned} unowned-skipped,{' '}
                    {r.sync_result.finance_field_detail_fetches ?? 0} financeField detail-fetches
                    (the list endpoint never carries commission data; only reservations
                    needing it get an extra per-reservation fetch)
                  </div>
                )}

                <p className="text-sm text-muted-foreground">{r.message}</p>

                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 className="h-4 w-4" />
                  Backfill complete. Check the Property Portfolio Bookings tab to verify.
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
